import { useState, type ReactNode } from "react";
import type { UploadRepairNotice } from "../../types";
import { WidescreenExportLauncher } from "../WidescreenExportLauncher";
import type { UploadResultsData } from "../../hooks/useUploadResultsReport";
import type { UploadResultsMetadata } from "../../hooks/useUploadResultsReport";
import { resolveUploadReportLayout } from "./reportLayoutResolver";

const USE_WHITE_BACKGROUND = true;

const BACKGROUND_CLASSES = USE_WHITE_BACKGROUND
  ? "bg-white dark:bg-[#2C2525]"
  : "bg-gray-50 dark:bg-[#2C2525]";

const NOTICE_LEVEL_ORDER: Record<UploadRepairNotice["level"], number> = {
  info: 0,
  warning: 1,
  error: 2,
};

const NOTICE_LEVEL_STYLES: Record<
  UploadRepairNotice["level"],
  { container: string; indicator: string }
> = {
  info: {
    container:
      "border-blue-200 bg-blue-50/80 text-blue-900 dark:border-blue-500/40 dark:bg-blue-950/30 dark:text-blue-100",
    indicator: "bg-blue-500",
  },
  warning: {
    container:
      "border-amber-200 bg-amber-50/90 text-amber-900 dark:border-amber-400/40 dark:bg-amber-900/30 dark:text-amber-100",
    indicator: "bg-amber-500",
  },
  error: {
    container:
      "border-rose-200 bg-rose-50/90 text-rose-900 dark:border-rose-500/40 dark:bg-rose-900/30 dark:text-rose-100",
    indicator: "bg-rose-500",
  },
};

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === "string");

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

function renderNoticeDetails(details?: Record<string, unknown>) {
  if (!details) return null;

  const elements: ReactNode[] = [];
  const handledKeys = new Set<string>();

  const missing = details["missing_fields"];
  if (isStringArray(missing) && missing.length > 0) {
    handledKeys.add("missing_fields");
    elements.push(
      <p key="missing-fields">
        Missing fields:{" "}
        <span className="font-mono text-xs">{missing.join(", ")}</span>
      </p>,
    );
  }

  const applied = details["fields"];
  if (isStringArray(applied) && applied.length > 0) {
    handledKeys.add("fields");
    elements.push(
      <p key="applied-fields">
        Applied mappings:{" "}
        <span className="font-mono text-xs">{applied.join(", ")}</span>
      </p>,
    );
  }

  const mapped = details["mapped_fields"];
  if (isRecord(mapped)) {
    handledKeys.add("mapped_fields");
    elements.push(
      <div key="mapped-fields">
        <p className="font-semibold">Mapped columns</p>
        <ul className="mt-1 list-disc space-y-1 pl-4">
          {Object.entries(mapped).map(([canonical, rawColumns]) => (
            <li key={canonical}>
              <span className="font-medium">{canonical}</span>{" "}
              <span className="text-xs font-mono">
                →
                {isStringArray(rawColumns)
                  ? ` ${rawColumns.join(", ")}`
                  : ` ${String(rawColumns)}`}
              </span>
            </li>
          ))}
        </ul>
      </div>,
    );
  }

  const confidence = details["confidence"];
  if (typeof confidence === "number") {
    handledKeys.add("confidence");
    elements.push(
      <p key="confidence">
        Confidence score:{" "}
        <span className="font-mono">{(confidence * 100).toFixed(1)}%</span>
      </p>,
    );
  }

  const recommendation = details["recommendation"];
  if (typeof recommendation === "string" && recommendation.trim().length > 0) {
    handledKeys.add("recommendation");
    elements.unshift(
      <p key="recommendation" className="text-sm font-medium">
        {recommendation}
      </p>,
    );
  }

  const remainingEntries = Object.entries(details).filter(
    ([key]) => !handledKeys.has(key),
  );
  if (remainingEntries.length > 0) {
    elements.push(
      <pre key="raw-details" className="rounded bg-black/5 p-2 text-[11px]">
        {JSON.stringify(Object.fromEntries(remainingEntries), null, 2)}
      </pre>,
    );
  }

  if (elements.length === 0) {
    return null;
  }

  return (
    <div className="mt-1 space-y-1 text-xs text-gray-600 dark:text-gray-200">
      {elements}
    </div>
  );
}

function NoticeList({ notices }: { notices: UploadRepairNotice[] }) {
  return (
    <ul className="space-y-3">
      {notices.map((notice, index) => (
        <li
          key={`${notice.level}-${index}-${notice.message}`}
          className="rounded-lg bg-white/40 p-3 text-sm text-slate-900 shadow-sm dark:bg-black/10 dark:text-slate-100"
        >
          <p className="font-medium">{notice.message}</p>
          {renderNoticeDetails(notice.details)}
        </li>
      ))}
    </ul>
  );
}

function RepairNoticePanel({ notices }: { notices: UploadRepairNotice[] }) {
  const [isCollapsed, setIsCollapsed] = useState(false);

  if (notices.length === 0) {
    return null;
  }

  const highestLevel = notices.reduce<UploadRepairNotice["level"]>(
    (acc, notice) =>
      NOTICE_LEVEL_ORDER[notice.level] > NOTICE_LEVEL_ORDER[acc]
        ? notice.level
        : acc,
    notices[0].level,
  );
  const panelStyles = NOTICE_LEVEL_STYLES[highestLevel];

  const hasAiAssistedNotice = notices.some((notice) => {
    const details = notice.details;
    if (!details || typeof details !== "object") return false;
    const keys = new Set(Object.keys(details));
    return (
      keys.has("mapped_fields") ||
      keys.has("unmapped_fields") ||
      keys.has("confidence") ||
      keys.has("recommended_normalizer") ||
      keys.has("raw_headers")
    );
  });

  const title =
    highestLevel === "info" && !hasAiAssistedNotice ? "Info" : "Data quality";
  const subtitle = hasAiAssistedNotice
    ? "We detected unfamiliar columns and used AI assistance to keep your dashboard usable. Review the notes below for context. This banner is omitted from exported decks."
    : highestLevel === "info"
      ? "We applied standard filtering/validation rules to match dashboard requirements. Review the notes below for context. This banner is omitted from exported decks."
      : "We detected potential data issues and applied safe fixes to keep your dashboard usable. Review the notes below for context. This banner is omitted from exported decks.";

  return (
    <div
      className="mx-auto w-full max-w-6xl px-4 pt-6"
      data-exclude-from-export
    >
      <div
        className={`rounded-2xl border px-5 py-4 shadow-sm ${panelStyles.container}`}
      >
        <div className="flex flex-wrap items-start gap-3">
          <div
            className={`mt-1 h-2.5 w-2.5 rounded-full ${panelStyles.indicator}`}
          />
          <div className="flex-1 min-w-[220px]">
            <p className="text-sm font-semibold tracking-wide">{title}</p>
            <p className="text-xs opacity-80">{subtitle}</p>
          </div>
          <button
            type="button"
            aria-expanded={!isCollapsed}
            onClick={() => setIsCollapsed((prev) => !prev)}
            className="ml-auto rounded-full border border-current px-3 py-1 text-xs font-semibold uppercase tracking-wide transition hover:bg-white/20"
          >
            {isCollapsed ? "Show details" : "Hide details"}
          </button>
        </div>

        {!isCollapsed && (
          <div className="mt-4">
            <NoticeList notices={notices} />
          </div>
        )}
      </div>
    </div>
  );
}

function RepairNoticeModal({
  notices,
  onDismiss,
}: {
  notices: UploadRepairNotice[];
  onDismiss: () => void;
}) {
  if (notices.length === 0) {
    return null;
  }

  const highestLevel = notices.reduce<UploadRepairNotice["level"]>(
    (acc, notice) =>
      NOTICE_LEVEL_ORDER[notice.level] > NOTICE_LEVEL_ORDER[acc]
        ? notice.level
        : acc,
    notices[0].level,
  );
  const modalStyles = NOTICE_LEVEL_STYLES[highestLevel];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-8 backdrop-blur-sm">
      <div
        className={`relative w-full max-w-3xl rounded-3xl border px-8 py-6 shadow-2xl ${modalStyles.container}`}
        role="dialog"
        aria-modal="true"
      >
        <h2 className="text-lg font-semibold tracking-wide">
          Important: Missing report data
        </h2>
        <p className="mt-1 text-sm opacity-80">
          We processed your upload, but some required fields were missing. Use
          the notes below to re-export the correct report so every dashboard
          visualization is available next time.
        </p>
        <div className="mt-4 space-y-3">
          <NoticeList notices={notices} />
        </div>
        <button
          type="button"
          className="mt-6 inline-flex items-center justify-center rounded-full bg-slate-900 px-5 py-2 text-sm font-semibold text-white shadow hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:bg-slate-100 dark:text-slate-900"
          onClick={onDismiss}
        >
          Got it
        </button>
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className={`min-h-screen flex items-center justify-center ${BACKGROUND_CLASSES}`}>
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600 dark:text-gray-400">Loading results...</p>
      </div>
    </div>
  );
}

type UploadResultsContentProps = {
  currentReportId: string | null;
  data: UploadResultsData | null;
  reportMetadata?: UploadResultsMetadata;
  handleNewUpload: () => void;
  reportProduct: string | null;
  setShowNoticeModal: (show: boolean) => void;
  showNoticeModal: boolean;
  viewMode: "original" | "slide" | "compact";
};

export function UploadResultsContent({
  currentReportId,
  data,
  reportMetadata,
  handleNewUpload,
  reportProduct,
  setShowNoticeModal,
  showNoticeModal,
  viewMode,
}: UploadResultsContentProps) {
  if (!data) {
    return <LoadingState />;
  }

  return (
    <div className={`min-h-screen ${BACKGROUND_CLASSES}`}>
      {data.repair_notices && data.repair_notices.length > 0 && (
        <RepairNoticePanel notices={data.repair_notices} />
      )}
      {showNoticeModal && data.repair_notices && (
        <RepairNoticeModal
          notices={data.repair_notices}
          onDismiss={() => setShowNoticeModal(false)}
        />
      )}
      {resolveUploadReportLayout({
        currentReportId,
        data,
        reportMetadata,
        handleNewUpload,
        reportProduct,
        viewMode,
      })}
      {currentReportId && <WidescreenExportLauncher reportId={currentReportId} />}
    </div>
  );
}
